// Groups the navigation links into collapsible categories, used only when the
// navbar is in its collapsed (small-screen) layout.
//
// Grouping is keyed on the backend `module` identifier (stable) rather than the
// route string or the display `name` (which comes from the DB and can change or
// be localized). If the backend adds a new module that isn't mapped here, its
// link falls back into a "More" group so no link is ever silently dropped.

// Ordered group definitions. Order here === order of the triggers in the navbar.
export const NAV_GROUP_DEFS = [
    {
        key: "resourcing",
        label: "Resourcing",
        modules: ["Planner", "Allocations", "Projects"],
    },
    {
        key: "myWork",
        label: "My Work",
        modules: ["Timesheets", "DailyTimesheet", "MyAllocations"],
    },
    {
        key: "more",
        label: "More",
        modules: ["BusinessIntelligence", "Admin"],
    },
];

// The "More" catch-all group. The hardcoded external links (Reporter, Provide
// Feedback) are appended here, and any backend module not mapped to a group
// above also lands here, so no link is ever silently dropped.
export const CATCH_ALL_GROUP_KEY = "more";

/**
 * Builds the ordered list of collapsed-mode groups from the resolved links.
 *
 * @param {Array} internalLinks - link descriptors of shape
 *        { kind: "internal", id, route, name, module }
 * @param {Array} externalLinks - link descriptors of shape
 *        { kind: "external", id, name, href, target?, rel? }
 * @returns {Array} groups of shape { key, label, children } with empty groups
 *          removed. `children` preserve internal-then-external ordering.
 */
export const buildNavGroups = (internalLinks = [], externalLinks = []) => {
    const byModule = new Map(
        internalLinks.filter((l) => l.module).map((l) => [l.module, l])
    );
    const mappedModules = new Set(
        NAV_GROUP_DEFS.flatMap((group) => group.modules)
    );

    // Internal links whose module isn't mapped anywhere above.
    const orphans = internalLinks.filter(
        (link) => !link.module || !mappedModules.has(link.module)
    );

    const groups = NAV_GROUP_DEFS.map((def) => {
        const children = def.modules
            .map((moduleId) => byModule.get(moduleId))
            .filter(Boolean);

        if (def.key === CATCH_ALL_GROUP_KEY) {
            // Orphans and external links collect in the catch-all group.
            children.push(...orphans, ...externalLinks);
        }

        return { key: def.key, label: def.label, children };
    });

    return groups.filter((group) => group.children.length > 0);
};
